// E8NumberLineAxisProductOpenTask.js
// Zadanie otwarte E8 (2025 CZE zad. 19) - dwie osie liczbowe, iloczyn wspolrzednych x*y.
// Os 1: K=0, L=vL (przy dziale dL), M=x (przy dziale 9). Os 2: P=0, R=y (dzial dR), S=vS (dzial 9).

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput from "../../../components/inputs/FractionInput";
import NumberLineAxis from "../../../components/diagrams/NumberLineAxis";
import { scoreStaged } from "../../openTaskScoring";

const DIVISIONS = 9;

const CASES = [
  { vL: 5, dL: 2, vS: 18, dR: 3 }, // original: x=22,5; y=6; iloczyn=135
  { vL: 4, dL: 2, vS: 27, dR: 2 }, // x=18; y=6; iloczyn=108
  { vL: 6, dL: 3, vS: 36, dR: 4 }, // x=18; y=16; iloczyn=288
  { vL: 3, dL: 2, vS: 45, dR: 2 }, // x=13,5; y=10; iloczyn=135
  { vL: 8, dL: 4, vS: 18, dR: 5 }, // x=18; y=10; iloczyn=180
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

function buildData(config) {
  const unit1 = config.vL / config.dL;
  const x = unit1 * DIVISIONS;
  const unit2 = config.vS / DIVISIONS;
  const y = unit2 * config.dR;
  const product = x * y;
  return { ...config, unit1, unit2, x, y, product, maxPoints: 2 };
}

function createOriginalTask() {
  return {
    type: "E8NumberLineAxisProductOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 19 },
    tags: ["e8", "os-liczbowa", "ulamki-dziesietne", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8NumberLineAxisProductOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_19" },
    tags: ["e8", "os-liczbowa", "ulamki-dziesietne", "zadanie-otwarte"],
  };
}

function NumberLines({ data }) {
  return (
    <div style={{ display: "grid", gap: 6, justifyItems: "center", margin: "14px 0" }}>
      <NumberLineAxis
        divisions={DIVISIONS}
        topLabels={[{ index: 0, math: "K" }, { index: data.dL, math: "L" }, { index: DIVISIONS, math: "M" }]}
        markers={[{ index: 0 }, { index: data.dL }, { index: DIVISIONS }]}
        bottomLabels={[
          { index: 0, math: "0" },
          { index: data.dL, math: formatNumber(data.vL) },
          { index: DIVISIONS, math: "x" },
        ]}
        ariaLabel="Pierwsza oś liczbowa z punktami K, L, M"
      />
      <NumberLineAxis
        divisions={DIVISIONS}
        topLabels={[{ index: 0, math: "P" }, { index: data.dR, math: "R" }, { index: DIVISIONS, math: "S" }]}
        markers={[{ index: 0 }, { index: data.dR }, { index: DIVISIONS }]}
        bottomLabels={[
          { index: 0, math: "0" },
          { index: data.dR, math: "y" },
          { index: DIVISIONS, math: formatNumber(data.vS) },
        ]}
        ariaLabel="Druga oś liczbowa z punktami P, R, S"
      />
    </div>
  );
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return String(value);
}

function normalize(raw) {
  return rawValue(raw).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function numberTokens(raw) {
  return (normalize(raw).match(/\d+(?:\.\d+)?/g) || []).map(Number);
}

function hasNumber(raw, target, tolerance = 0.01) {
  return numberTokens(raw).some((value) => Math.abs(value - target) <= tolerance);
}

function parseNumberLike(value) {
  const raw = normalize(value);
  if (!raw) return null;
  if (raw.includes("/")) {
    const [a, b] = raw.split("/").map(Number);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    return a / b;
  }
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Dane są dwie osie liczbowe (zobacz rysunek).</p>
      <p>
        Na pierwszej z nich zaznaczono punkty <InlineMath math="K,\\ L,\\ M" /> oraz podano
        współrzędne punktów <InlineMath math="K" /> i <InlineMath math="L" />. Odcinek{" "}
        <InlineMath math="KM" /> jest podzielony na <InlineMath math="9" /> równych części.
      </p>
      <p>
        Na drugiej osi liczbowej zaznaczono punkty <InlineMath math="P,\\ R,\\ S" /> oraz podano
        współrzędne punktów <InlineMath math="P" /> i <InlineMath math="S" />. Odcinek{" "}
        <InlineMath math="PS" /> jest podzielony na <InlineMath math="9" /> równych części.
      </p>
      <NumberLines data={data} />
      <p>
        <strong>Oblicz iloczyn <InlineMath math="x\\cdot y" />. Zapisz obliczenia.</strong>
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Wyznacz współrzędną <InlineMath math="x" /> punktu <InlineMath math="M" />.</div>
        <EquationInput value={userAnswer.x || ""} onChange={setField("x")} disabled={disabled} placeholder="np. 5/2*9=22,5" showPreview basis="380px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Wyznacz współrzędną <InlineMath math="y" /> punktu <InlineMath math="R" />.</div>
        <EquationInput value={userAnswer.y || ""} onChange={setField("y")} disabled={disabled} placeholder="np. 18/9*3=6" showPreview basis="380px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 3. Wpisz iloczyn <InlineMath math="x\\cdot y" />.</div>
        <FractionInput value={userAnswer.product} onChange={setField("product")} disabled={disabled} placeholder="np. 135" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const coordsOk = hasNumber(userAnswer.x, data.x) && hasNumber(userAnswer.y, data.y);
  const result = parseNumberLike(userAnswer.product);
  const productOk = result != null && Math.abs(result - data.product) <= 0.01;
  return scoreStaged({
    resultOk: productOk,
    milestones: [coordsOk],
    maxScore: data.maxPoints,
    parts: { "współrzędne x i y": coordsOk, "iloczyn": productOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <BlockMath math={`x=${formatNumber(data.x)},\\quad y=${formatNumber(data.y)}`} />
      <div>
        {"Odpowied\u017a: iloczyn "}
        <InlineMath
          math={`x\\cdot y=${formatNumber(data.x)}\\cdot ${formatNumber(data.y)}=${formatNumber(data.product)}`}
        />{"."}
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const result = parseNumberLike(userAnswer.product);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>x: {userAnswer.x || <i>(brak)</i>}</div>
      <div>y: {userAnswer.y || <i>(brak)</i>}</div>
      <div>Iloczyn: {result == null ? <i>(brak)</i> : formatNumber(result)}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Odcinek <InlineMath math="KM" /> podzielono na 9 części.
        Punkt <InlineMath math="L" /> leży na <InlineMath math={`${data.dL}`} /> działce i ma
        współrzędną <InlineMath math={`${formatNumber(data.vL)}`} />, więc długość jednej działki to:
      </p>
      <BlockMath math={`${formatNumber(data.vL)}:${data.dL}=${formatNumber(data.unit1)}`} />
      <BlockMath math={`x=${formatNumber(data.unit1)}\\cdot ${DIVISIONS}=${formatNumber(data.x)}`} />
      <p>
        <strong>Krok 2.</strong> Na drugiej osi <InlineMath math={`S=${formatNumber(data.vS)}`} /> leży
        na 9 działce, więc jedna działka ma długość:
      </p>
      <BlockMath math={`${formatNumber(data.vS)}:${DIVISIONS}=${formatNumber(data.unit2)}`} />
      <BlockMath math={`y=${formatNumber(data.unit2)}\\cdot ${data.dR}=${formatNumber(data.y)}`} />
      <p><strong>Krok 3.</strong> Obliczamy iloczyn:</p>
      <BlockMath math={`x\\cdot y=${formatNumber(data.x)}\\cdot ${formatNumber(data.y)}=${formatNumber(data.product)}`} />
    </div>
  );
}
